/**
 * Número de WhatsApp: estado, calidad y límite actual, más el límite en el tiempo (línea
 * escalonada) sobre franjas de color por calidad. Meta no avisa estos cambios por evento: el
 * histórico lo arma el producto con los snapshots del refresco del número (§1.1).
 *
 * «Sin límite» no tiene número: se dibuja en la punta superior con la etiqueta «Sin límite».
 *
 * Con la cuenta conectada y sin número (`sin_numero`), la cuenta viene disponible pero el número no:
 * se dice eso, en vez de «Sin datos del número todavía» o del sender que dejó el simulador.
 */

import { CartesianGrid, Line, LineChart, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { EstadisticasTwilio } from "../../types.estadisticas";
import { COLOR_CALIDAD, COLORES, esSinNumero, normalizarCalidad, serieLimite, tramosCalidad } from "../../logic/estadisticas";
import { calidadInfo, ESTADO_NUMERO } from "../../logic/textos";
import { fechaHora, num } from "../../../campana-nueva/utils/format";
import { AvisoConectar, ESTILO_TOOLTIP, Leyenda } from "./comun";

export function TarjetaNumero({ twilio, ahora = new Date() }: { twilio: EstadisticasTwilio; ahora?: Date }) {
  if (!twilio.disponible || esSinNumero(twilio.motivoNoDisponible)) return <AvisoConectar motivo={twilio.motivoNoDisponible} />;
  const a = twilio.sender?.actual;
  const { puntos, techo } = serieLimite(twilio.sender?.historial);
  const tramos = tramosCalidad(twilio.sender?.historial, ahora);
  // Senders v2 manda HIGH/MEDIUM/LOW («"HIGH"» en el ejemplo de la doc): se normaliza antes de
  // buscar el texto, si no un HIGH se leería «Sin dato».
  const cal = calidadInfo(normalizarCalidad(a?.calidad), a?.calidadRaw);
  // El valor crudo de Twilio va a la vista: un valor que no reconocemos (D-15) se lee «Sin dato» en
  // el chip, y sin el crudo el operador no sabría qué dijo Meta.
  const calidadCruda = String(a?.calidadRaw || (normalizarCalidad(a?.calidad) === "UNKNOWN" ? a?.calidad || "" : "")).trim();
  // Con el que cargó el equipo (Twilio dice «Unavailable»), ése: es el que usa el motor. «Sin dato de Meta» decía lo contrario.
  const eq = a?.limiteDelEquipo;
  const limite = !a
    ? "—"
    : eq
      ? (eq.ilimitado ? "Sin límite" : `${num(Number(eq.limite))} personas cada 24 h`)
      : a.messagingLimitIlimitado ? "Sin límite" : a.messagingLimit !== null ? `${num(a.messagingLimit)} personas cada 24 h` : "Sin dato de Meta";
  const estado = a ? (ESTADO_NUMERO[String(a.estado).toUpperCase()] ?? a.estado) : "Sin datos del número todavía";
  const calidadesVistas = [...new Set(tramos.map((t) => t.calidad))];
  // Con algún tramo «sin límite», las marcas del eje se fijan para que la punta superior (donde se
  // dibuja «Sin límite») tenga su etiqueta; si no, recharts elige marcas que no la tocan.
  const hayIlimitado = puntos.some((x) => x.ilimitado);
  const ticks = hayIlimitado ? [0, Math.round(techo / 3), Math.round((2 * techo) / 3), techo] : undefined;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="rounded-xl bg-slate-50 px-3 py-2.5">
          <p className="text-sm font-semibold text-slate-900">{estado}</p>
          <p className="text-xs text-slate-600">Estado{a?.nombreVisible ? ` · ${a.nombreVisible}` : ""}</p>
        </div>
        <div className="rounded-xl bg-slate-50 px-3 py-2.5">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cal.cls}`}>{cal.label}</span>
          <p className="text-xs text-slate-600 mt-1">Calidad según Meta{calidadCruda ? ` · Twilio: «${calidadCruda}»` : ""}</p>
        </div>
        <div className="rounded-xl bg-slate-50 px-3 py-2.5">
          <p className="text-sm font-semibold text-slate-900">{limite}</p>
          <p className="text-xs text-slate-600">Límite actual{eq ? " · lo cargó el equipo" : ""}{a?.messagingLimitRaw ? ` · Twilio: «${a.messagingLimitRaw}»` : ""}</p>
        </div>
      </div>

      {puntos.length > 1 ? (
        <div className="space-y-2">
          <Leyenda items={[
            { color: COLORES.limite, label: "Límite de personas cada 24 h", linea: true },
            ...calidadesVistas.map((c) => ({ color: COLOR_CALIDAD[c], label: `Calidad ${calidadInfo(c).label.toLowerCase()}` })),
          ]} />
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={puntos} margin={{ top: 16, right: 8, bottom: 0, left: -4 }}>
                {tramos.map((t) => <ReferenceArea key={`${t.desde}-${t.calidad}`} x1={t.desde} x2={t.hasta} fill={t.color} fillOpacity={1} ifOverflow="extendDomain" />)}
                <CartesianGrid stroke={COLORES.grilla} vertical={false} />
                <XAxis dataKey="t" type="number" scale="time" domain={["dataMin", "dataMax"]} tick={{ fontSize: 11, fill: COLORES.eje }} tickLine={false} axisLine={false}
                  tickFormatter={(v) => new Date(Number(v)).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" })} minTickGap={24} />
                <YAxis domain={[0, techo]} ticks={ticks} tick={{ fontSize: 11, fill: COLORES.eje }} tickLine={false} axisLine={false} width={56}
                  tickFormatter={(v) => (Number(v) >= techo ? "Sin límite" : Number(v).toLocaleString("es-AR"))} />
                <Tooltip {...ESTILO_TOOLTIP} labelFormatter={(v) => fechaHora(new Date(Number(v)))}
                  formatter={(v, _n, item) => [(item?.payload as { ilimitado?: boolean } | undefined)?.ilimitado ? "Sin límite" : Number(v).toLocaleString("es-AR"), "Límite"]} />
                <Line type="stepAfter" dataKey="limite" name="Límite" stroke={COLORES.limite} strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-400">Todavía no hay historia del número: se arma con cada revisión (una por hora).</p>
      )}
      {a?.actualizadoAt && <p className="text-[11px] text-slate-400">Última revisión: {fechaHora(a.actualizadoAt)}</p>}
    </div>
  );
}

export default TarjetaNumero;
